import { ChakraProvider } from '@chakra-ui/react';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { ColorSchemeName, validateColorSchemeName } from '@/config/themeConfig';
import { createDynamicTheme } from './dynamicTheme';
import { API } from '@/api';

const THEME_STORAGE_KEY = 'app-color-scheme';

interface ThemeContextValue {
  colorScheme: ColorSchemeName;
  setColorScheme: (scheme: ColorSchemeName) => void;
  isLoading: boolean;
  environment?: string;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const DynamicThemeProvider = ({ children }: ThemeProviderProps) => {
  const [colorScheme, setColorSchemeState] = useState<ColorSchemeName>(() => {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored) {
        return validateColorSchemeName(stored, 'green');
      }
    }
    return 'green';
  });
  const [environment, setEnvironment] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTheme = async () => {
      try {        
        const themeData = await API.theme.get();
        setEnvironment(themeData.environment);
        const dbScheme = validateColorSchemeName(themeData.colorScheme, 'green');
        
        const cached = localStorage.getItem(THEME_STORAGE_KEY);
        if (cached !== dbScheme) {
          setColorSchemeState(dbScheme);
          localStorage.setItem(THEME_STORAGE_KEY, dbScheme);
        }
        
      } catch (error) {
        console.error('Failed to fetch theme from database:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTheme();
  }, []);

  const setColorScheme = (scheme: ColorSchemeName) => {
    setColorSchemeState(scheme);
    localStorage.setItem(THEME_STORAGE_KEY, scheme);
  };

  const theme = createDynamicTheme(colorScheme);

  return (
    <ThemeContext.Provider value={{ colorScheme, setColorScheme, isLoading, environment }}>
      <ChakraProvider theme={theme}>{children}</ChakraProvider>
    </ThemeContext.Provider>
  );
};
